import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);
const KNOWN_ACCOUNTS_KEY = "stylio:known-accounts";

const readKnownAccounts = () => {
  try { return JSON.parse(localStorage.getItem(KNOWN_ACCOUNTS_KEY) || "[]"); } catch { return []; }
};
const writeKnownAccounts = (list) => localStorage.setItem(KNOWN_ACCOUNTS_KEY, JSON.stringify(list));

const upsertKnownAccount = (u) => {
  if (!u?.user_id) return;
  const list = readKnownAccounts();
  const filtered = list.filter((a) => a.user_id !== u.user_id);
  // Prefer the user's *uploaded* avatar (`avatar_url`) over the Google
  // OAuth `picture` — otherwise a freshly-uploaded photo never shows in the
  // account switcher because we cached the Google fallback at first login.
  filtered.unshift({
    user_id: u.user_id,
    name: u.name || "",
    username: u.username || "",
    email: u.email || "",
    picture: u.avatar_url || u.picture || "",
    last_used: new Date().toISOString(),
  });
  writeKnownAccounts(filtered.slice(0, 5));
};

export const getKnownAccounts = () => readKnownAccounts();
export const removeKnownAccount = (user_id) => {
  writeKnownAccounts(readKnownAccounts().filter((a) => a.user_id !== user_id));
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // A localStorage-persisted override so the client can mark itself onboarded
  // even if the backend hasn't caught up yet. Fixes the native APK bug where
  // /users/me/mark-onboarded races with /auth/me and users get bounced back
  // to onboarding forever.
  const applyOnboardedOverride = (data) => {
    if (!data) return data;
    try {
      if (localStorage.getItem("sb:onboarded") === "1") {
        return { ...data, onboarded: true };
      }
    } catch { /* Safari private mode etc. */ }
    return data;
  };

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(applyOnboardedOverride(data));
      upsertKnownAccount(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout request failed; clearing local session anyway.", e);
      }
    }
    // Also clear the cached Google account inside the native SDK so the
    // NEXT `signIn()` re-prompts the account picker instead of silently
    // returning the last-used Gmail. No-op on web.
    try {
      const { nativeGoogleSignOut } = await import("@/lib/native");
      await nativeGoogleSignOut();
    } catch { /* non-fatal — local session still clears below */ }
    try { localStorage.removeItem("sb:onboarded"); } catch { /* ignore */ }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, refresh: fetchMe, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
